import { MutationStatus, QueryKey, RefetchOptions, useQuery, UseQueryOptions } from 'react-query';

import { isAClientError, isUnauthorizedError } from '#lib/network/helpers';

const MAX_RETRY = 3;

export type Fetcher<T> = () => Promise<T>;

export type UseGetOptions = {
  avoidRetry?: boolean;
} & UseQueryOptions;

export interface UseAPIResult<T> {
  data?: T;
  isLoading: boolean;
  error: any;
  isError: boolean;
  refetch: (options?: RefetchOptions) => Promise<any>;
  status: MutationStatus;
  usedQueryKey: string[];
}

export const useGet = <T extends any>(
  uniqueKey: QueryKey,
  fetcher: Fetcher<T>,
  options?: UseGetOptions
): UseAPIResult<T> => {
  const { isLoading, error, isError, data, refetch, status } = useQuery<T>(uniqueKey, fetcher, {
    notifyOnChangeProps: ['data', 'error'],
    retry: (failureCount: number, error) => {
      if (options?.avoidRetry) {
        return false;
      }

      if (error && isAClientError(error)) {
        return false;
      }

      if (error && isUnauthorizedError(error)) {
        return false;
      }

      return failureCount < MAX_RETRY;
    },
    ...(options as any)
  });

  return {
    data,
    isLoading,
    error,
    refetch,
    isError,
    status,
    usedQueryKey: uniqueKey as string[]
  };
};
